import { useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Seo from "@/components/feature/Seo";
import EventCard from "@/components/feature/EventCard";
import { categories, events, type GuaEvent } from "@/mocks/events";

export default function EventsPage() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "all";
  const q = params.get("q") || "";

  const filtered = useMemo(() => {
    let list: GuaEvent[] = events;
    if (cat !== "all") list = list.filter((e) => e.category === cat);
    if (q.trim()) {
      const kw = q.trim().toLowerCase();
      list = list.filter(
        (e) =>
          e.title.toLowerCase().includes(kw) ||
          e.summary.toLowerCase().includes(kw) ||
          e.tags.some((t) => t.toLowerCase().includes(kw)),
      );
    }
    return list;
  }, [cat, q]);

  const setCategory = (key: string) => {
    const next = new URLSearchParams(params);
    if (key === "all") next.delete("cat");
    else next.set("cat", key);
    setParams(next, { replace: true });
  };

  const jsonLd = useMemo(() => {
    const base = window.location.origin;
    return [
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: base },
          { "@type": "ListItem", position: 2, name: "事件库", item: `${base}/events` },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: q ? `“${q}” 的搜索结果` : "国产吃瓜视频事件库",
        url: `${base}/events`,
        description: "浏览国产吃瓜视频最新热点事件、八卦汇总与分类速递，持续更新。",
        inLanguage: "zh-CN",
        hasPart: filtered.map((e) => ({
          "@type": "WebPage",
          name: e.title,
          url: `${base}/event/${e.id}`,
        })),
      },
    ];
  }, [filtered, q]);

  const activeCat = categories.find((c) => c.key === cat);

  return (
    <>
      <Seo
        title={q ? `搜索“${q}”的吃瓜热点事件 - 吃瓜速递` : "国产吃瓜视频事件库 · 热点事件与八卦汇总 - 吃瓜速递"}
        description="进入国产吃瓜视频事件库，浏览明星动态、娱乐八卦、综艺现场、网络红人与社会热点事件，收藏本站每日刷新获取最新八卦汇总速递。"
        keywords="国产吃瓜视频,热点事件,八卦汇总,事件库,分类速递"
        path="/events"
        jsonLd={jsonLd}
      />

      <div className="bg-foreground-950 pb-12 pt-24 md:pb-16 md:pt-28">
        <div className="container-x">
          <nav aria-label="面包屑" className="mb-5 flex items-center gap-1.5 text-sm text-background-50/60">
            <Link to="/" className="transition-colors hover:text-background-50">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">事件库</span>
          </nav>
          <h1 className="font-heading text-2xl font-bold text-background-50 md:text-3xl">
            {q ? `“${q}” 的搜索结果` : "国产吃瓜视频 · 热点事件库"}
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-6 text-background-50/75">
            {q
              ? `共找到 ${filtered.length} 条与“${q}”相关的事件与八卦汇总。`
              : "按栏目浏览近期最热吃瓜事件，想看最新热点或完整汇总，收藏本站每日刷新即可。"}
          </p>
        </div>
      </div>

      <section className="container-x py-8 md:py-12">
        <div className="flex flex-wrap items-center gap-2">
          {categories.map((c) => (
            <button
              key={c.key}
              type="button"
              onClick={() => setCategory(c.key)}
              className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                cat === c.key
                  ? "bg-primary-500 text-background-50"
                  : "bg-background-100 text-foreground-700 hover:bg-primary-100 hover:text-primary-700"
              }`}
            >
              <i className={c.icon}></i>
              {c.label}
            </button>
          ))}
        </div>

        <p className="mt-4 text-sm text-foreground-500">
          {activeCat && cat !== "all" ? activeCat.desc : "实时聚合全部热门吃瓜话题"}
          <span className="ml-2 text-foreground-400">· 共 {filtered.length} 条事件</span>
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((item) => (
              <EventCard key={item.id} item={item} />
            ))}
          </div>
        ) : (
          <div className="card mt-6 flex flex-col items-center justify-center py-16 text-center">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-2xl text-foreground-400">
              <i className="ri-search-eye-line"></i>
            </span>
            <p className="mt-4 text-sm font-medium text-foreground-700">没有找到相关事件</p>
            <p className="mt-1 text-xs text-foreground-500">换个关键词，或切换其他栏目再试试吧。</p>
            <button
              type="button"
              onClick={() => {
                setParams({ cat: "all" }, { replace: true });
              }}
              className="btn-primary mt-5"
            >
              查看全部事件
            </button>
          </div>
        )}
      </section>
    </>
  );
}